import { z } from 'zod';

/**
 * Wire protocol shared by the Thursday Night site frontend (site/src) and
 * its Cloudflare Worker + Durable Object backend (site/worker). Mirrors the VR
 * app's discipline: every payload is Zod-validated on the server — the worker
 * NEVER trusts a client. Codes that act as auth (event id, voter code, host
 * token) are minted server-side from crypto-random bytes and are unguessable.
 *
 * Privacy line (CLAUDE.md): no PII beyond a display name. Voters identify
 * themselves only by an opaque code the host handed them.
 */

export const PROTOCOL_VERSION = 1;

export const MAX_TITLE_LEN = 80;
export const MAX_NAME_LEN = 32;
export const MAX_PLACE_LEN = 200;
export const MAX_NOTE_LEN = 500;
export const MAX_OPTIONS = 5; // how many the host seeds at creation time
export const MIN_OPTIONS = 1;
export const DEFAULT_OPTIONS = 3;
// Ballot ceiling once invitee suggestions pile on top of the host's seed list.
export const MAX_TOTAL_OPTIONS = 20;
export const MAX_INVITEES = 12; // owner + crew, with headroom
// Upper bound for a single person's suggestion allowance (host sets each person's,
// 0 = not allowed to suggest). Keeps the ballot from being flooded by one person.
export const MAX_SUGGEST_PER_PERSON = 10;

// ---------- primitives ----------

const trimmed = (max: number) => z.string().trim().min(1).max(max);

/** A short lowercase slug naming a service or shelf ('netflix', 'plex', 'made-up'). */
const sourceSlug = z
  .string()
  .trim()
  .toLowerCase()
  .max(32)
  .regex(/^[a-z0-9][a-z0-9-]*$/);

/** ISO 8601 datetime string for the event start (local time of the host). */
export const isoDateTime = z.string().datetime({ offset: true }).or(z.string().datetime());

/** A recurrence rule — kept deliberately small (the crew meets weekly). */
export const recurrenceSchema = z.object({
  freq: z.enum(['none', 'weekly', 'biweekly', 'monthly']),
  // 0=Sun..6=Sat — only meaningful for weekly/biweekly; echoed for display
  weekday: z.number().int().min(0).max(6).optional(),
});
export type Recurrence = z.infer<typeof recurrenceSchema>;

/**
 * The invented, deadpan "movie facts" the enrichment writes for a made-up movie —
 * shown on the ballot card as the bit. Poster art itself is a normal `posterUrl`.
 */
export const posterMetaSchema = z.object({
  tagline: z.string().max(140).optional(),
  plot: z.string().max(MAX_NOTE_LEN).optional(),
  year: z.number().int().min(1880).max(2100).optional(),
  genre: z.string().max(60).optional(),
});
export type PosterMeta = z.infer<typeof posterMetaSchema>;

/** Bake lifecycle for an auto-generated parody poster (see the poster pipeline). */
export const artStatusSchema = z.enum(['pending', 'baking', 'ready', 'blocked', 'failed']);
export type ArtStatus = z.infer<typeof artStatusSchema>;

/** A movie/show option the crew can vote on. */
export const optionSchema = z.object({
  id: z.string().min(1).max(40), // server-minted slug, stable for the event's life
  title: trimmed(MAX_TITLE_LEN),
  year: z.number().int().min(1880).max(2100).optional(),
  // poster/key-art URL. From TMDB/fanart.tv, a host-pasted image URL, OR — for a
  // made-up movie with no match — a parody poster baked on the owner's box.
  // Validated as https to avoid mixed-content.
  posterUrl: z.string().url().startsWith('https://').max(500).optional(),
  // TMDB id when we resolved one — lets us re-fetch art later if desired
  tmdbId: z.number().int().positive().optional(),
  overview: z.string().max(MAX_NOTE_LEN).optional(),
  // For a MADE-UP movie with no match: the suggester's own "what's it about" blurb.
  // Steers the parody enrichment/poster and shows as the description while it bakes.
  hint: z.string().max(280).optional(),
  // Display name of the invitee who suggested this option (host-added options leave
  // it unset). Name ONLY — the suggester's secret code never leaves the server.
  suggestedByName: trimmed(MAX_NAME_LEN).optional(),
  // Where the suggester found it — 'netflix', 'plex', 'tmdb', 'made-up'. A free
  // slug rather than an enum ON PURPOSE: which services exist is still open
  // (THU-128), and an enum would have to be widened in the shared protocol every
  // time one is added, which is a wire change to add a label. Unset on anything
  // suggested before this field existed and on everything the host seeds.
  source: sourceSlug.optional(),
  // When it joined the ballot. Server-stamped — see optionDraftSchema, which
  // omits it so a client cannot backdate its own suggestion.
  suggestedAt: z.string().datetime().optional(),
  // ---- parody-poster bake (only for made-up movies with no real art) ----
  // undefined = a normal option; otherwise the poster oven's lifecycle stage.
  art: artStatusSchema.optional(),
  // the invented facts the enrichment wrote (tagline/plot/year/genre) — the bit.
  poster: posterMetaSchema.optional(),
  // host/suggester gave the baked poster a thumbs-up (hides the "review" nudge).
  artApproved: z.boolean().optional(),
});
export type MovieOption = z.infer<typeof optionSchema>;

/** A brand-new option proposed by client input — server mints the id + provenance. */
export const optionDraftSchema = optionSchema.omit({
  id: true,
  suggestedByName: true,
  suggestedAt: true,
});
export type OptionDraft = z.infer<typeof optionDraftSchema>;

/** A host-edited option: existing ones resend their id (kept + updated), new ones omit it. */
export const optionEditSchema = optionDraftSchema.extend({
  id: z.string().min(1).max(40).optional(),
});
export type OptionEdit = z.infer<typeof optionEditSchema>;

/** An invitee record — gets a unique code + personal link back to vote. */
export const inviteeSchema = z.object({
  code: z.string().min(8).max(40), // crypto-random; this IS their identity + auth
  name: trimmed(MAX_NAME_LEN),
});
export type Invitee = z.infer<typeof inviteeSchema>;

// ---------- create event (host → worker) ----------

export const createEventSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  title: trimmed(MAX_TITLE_LEN),
  startsAt: isoDateTime,
  place: trimmed(MAX_PLACE_LEN), // the VR room link or code
  recurrence: recurrenceSchema,
  note: z.string().max(MAX_NOTE_LEN).optional(),
  options: z.array(optionDraftSchema).min(MIN_OPTIONS).max(MAX_OPTIONS),
  // display names of people to invite; worker mints a code per name
  invitees: z.array(trimmed(MAX_NAME_LEN)).max(MAX_INVITEES).default([]),
  // optional Discord webhook the host pasted — worker can post the invite there
  // and ping when votes arrive. Stored server-side, never returned to clients.
  discordWebhook: z.string().url().startsWith('https://discord.com/api/webhooks/').max(300).optional(),
});
export type CreateEvent = z.infer<typeof createEventSchema>;

// ---------- edit event details (host → worker, host-token gated) ----------
// Editable fields are the night's DETAILS only — title, time, place, recurrence,
// note. Options + invitees aren't edited here (changing them would orphan votes /
// re-mint codes); re-create for those. Host token is passed as ?token= like the
// host view, not in the body.
export const editEventSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  title: trimmed(MAX_TITLE_LEN),
  startsAt: isoDateTime,
  place: trimmed(MAX_PLACE_LEN),
  recurrence: recurrenceSchema,
  note: z.string().max(MAX_NOTE_LEN).optional(),
});
export type EditEvent = z.infer<typeof editEventSchema>;

// ---------- edit movies (host → worker, host-token gated) ----------
// Full reconcile of the ballot: options that resend their id are kept (and their
// title/art updated, votes + suggestedBy provenance preserved); id-less options are
// minted fresh; existing options NOT resent are removed and any votes for them cleared.
export const manageOptionsSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  options: z.array(optionEditSchema).min(MIN_OPTIONS).max(MAX_TOTAL_OPTIONS),
});
export type ManageOptions = z.infer<typeof manageOptionsSchema>;

// ---------- suggest a movie (invitee → worker, voter-code gated) ----------
// An enabled invitee proposes ONE option; it joins the ballot immediately. The
// server enforces the per-person allowance and the overall ballot ceiling.
export const suggestOptionSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  code: z.string().min(8).max(40), // the suggester's personal code
  option: optionDraftSchema,
});
export type SuggestOption = z.infer<typeof suggestOptionSchema>;

// (suggestResultSchema lives further down — it embeds publicEventSchema, and a
// `const` is not hoisted, so declaring it here would ReferenceError on load.)

// ---------- withdraw a suggestion (invitee → worker, voter-code gated) ----------
// A suggester takes their OWN option back off the ballot. Refused once anyone
// else has voted for it — see EventRoom.withdraw for why that line is there.
export const withdrawOptionSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  code: z.string().min(8).max(40),
  optionId: z.string().min(1).max(40),
});
export type WithdrawOption = z.infer<typeof withdrawOptionSchema>;

// ---------- manage invitees (host → worker, host-token gated) ----------
// Add people after the fact, remove someone (revokes their link + drops their vote),
// or set a person's suggestion allowance (0 = can't suggest).
export const manageInviteesSchema = z.discriminatedUnion('op', [
  z.object({
    v: z.literal(PROTOCOL_VERSION),
    op: z.literal('add'),
    names: z.array(trimmed(MAX_NAME_LEN)).min(1).max(MAX_INVITEES),
  }),
  z.object({
    v: z.literal(PROTOCOL_VERSION),
    op: z.literal('remove'),
    code: z.string().min(8).max(40),
  }),
  z.object({
    v: z.literal(PROTOCOL_VERSION),
    op: z.literal('setSuggest'),
    code: z.string().min(8).max(40),
    limit: z.number().int().min(0).max(MAX_SUGGEST_PER_PERSON),
  }),
]);
export type ManageInvitees = z.infer<typeof manageInviteesSchema>;

// ---------- parody-poster review / regenerate (host-token OR suggester-code) ----------
// The "Review AI Image" quick-tag chips, mirrored from finewood's AiImagePanel so the
// owner gets the same feedback loop. Notes ride along separately and the server folds
// them into the finewood `feedback[]` shape (a trailing `notes:<text>` entry).
export const FEEDBACK_FLAGS = [
  'wrong-subject',
  'too-busy',
  'wrong-style',
  'bad-composition',
  'low-quality',
  'wrong-colors',
  'missing-parts',
  'not-recognizable',
  'weird-text',
  'cropped-weirdly',
  'too-dark',
  'too-bright',
  'creepy',
  'wrong-everything',
] as const;
export const feedbackFlagSchema = z.enum(FEEDBACK_FLAGS);
export type FeedbackFlag = (typeof FEEDBACK_FLAGS)[number];

/** Re-bake a parody poster with feedback. Host auths via ?token=; a suggester sends
 * their own `code` and may only regenerate their own suggestion. */
export const regeneratePosterSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  optionId: z.string().min(1).max(40),
  code: z.string().min(8).max(40).optional(),
  feedback: z.array(feedbackFlagSchema).max(FEEDBACK_FLAGS.length).default([]),
  notes: z.string().max(200).optional(),
});
export type RegeneratePoster = z.infer<typeof regeneratePosterSchema>;

/** Approve a baked poster (cosmetic — hides the review nudge). Same auth as regenerate. */
export const approveArtSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  optionId: z.string().min(1).max(40),
  code: z.string().min(8).max(40).optional(),
});
export type ApproveArt = z.infer<typeof approveArtSchema>;

// ---------- public event view (worker → anyone with the link) ----------

/** What a voter sees: the event + options + live tallies. No host secrets. */
export const publicEventSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  startsAt: z.string(),
  place: z.string(),
  recurrence: recurrenceSchema,
  note: z.string().optional(),
  options: z.array(optionSchema),
  // optionId -> vote count
  tally: z.record(z.string(), z.number().int().nonnegative()),
  totalVotes: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type PublicEvent = z.infer<typeof publicEventSchema>;

/** The host's view also includes the per-invitee links and the host token. */
export const hostEventSchema = publicEventSchema.extend({
  hostToken: z.string().min(1),
  invitees: z.array(
    inviteeSchema.extend({
      voted: z.boolean(),
      votedOptionId: z.string().optional(),
      // how many movies this person may suggest (0 = not allowed) + how many they
      // have already used, so the dashboard can show "suggested 1/2".
      suggestLimit: z.number().int().min(0).max(MAX_SUGGEST_PER_PERSON),
      suggestionsUsed: z.number().int().nonnegative(),
    }),
  ),
  hasDiscordWebhook: z.boolean(),
});
export type HostEvent = z.infer<typeof hostEventSchema>;

export const createEventResultSchema = z.object({
  event: hostEventSchema,
});
export type CreateEventResult = z.infer<typeof createEventResultSchema>;

// ---------- voting (invitee → worker) ----------

export const castVoteSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  code: z.string().min(8).max(40), // the invitee's personal code
  optionId: z.string().min(1).max(40),
});
export type CastVote = z.infer<typeof castVoteSchema>;

export const voteResultSchema = z.object({
  ok: z.literal(true),
  event: publicEventSchema,
  votedOptionId: z.string(),
  voterName: z.string(),
});
export type VoteResult = z.infer<typeof voteResultSchema>;

/**
 * What came back from a suggestion — and specifically WHICH of the two things
 * happened, because they are not the same event and the page must not guess.
 *
 * `merged: true` means the film was already on the ballot: no option was added,
 * the suggester's vote was moved onto the existing one, and their allowance was
 * NOT spent. `suggestionsUsed` is the server's count either way, because the
 * page used to increment its own counter optimistically on every call — which is
 * right for an add and wrong for a merge, and the wrongness shows up as a
 * suggestion box that claims an allowance is spent when it is not.
 */
export const suggestResultSchema = z.object({
  ok: z.literal(true),
  merged: z.boolean(),
  optionId: z.string().min(1).max(40),
  suggestionsUsed: z.number().int().nonnegative(),
  event: publicEventSchema,
});
export type SuggestResult = z.infer<typeof suggestResultSchema>;

/** What came back from a withdrawal: the ballot without it, and the freed allowance. */
export const withdrawResultSchema = z.object({
  ok: z.literal(true),
  suggestionsUsed: z.number().int().nonnegative(),
  event: publicEventSchema,
});
export type WithdrawResult = z.infer<typeof withdrawResultSchema>;

/** Who a personal code belongs to + their suggestion allowance (for the voter landing). */
export const whoamiSchema = z.object({
  name: z.string(),
  votedOptionId: z.string().optional(),
  suggestLimit: z.number().int().min(0).max(MAX_SUGGEST_PER_PERSON),
  suggestionsUsed: z.number().int().nonnegative(),
});
export type WhoAmI = z.infer<typeof whoamiSchema>;

// ---------- media search proxy (frontend → worker → TMDB/fanart.tv) ----------

export const mediaSearchResultSchema = z.object({
  tmdbId: z.number().int().positive(),
  title: z.string(),
  year: z.number().int().optional(),
  // best art we could resolve: fan-art (Plex-style) preferred, standard poster fallback
  posterUrl: z.string().url().optional(),
  overview: z.string().optional(),
  mediaType: z.enum(['movie', 'tv']),
});
export type MediaSearchResult = z.infer<typeof mediaSearchResultSchema>;

export const mediaSearchResponseSchema = z.object({
  // false when TMDB/fanart keys aren't configured — UI shows manual-entry mode
  configured: z.boolean(),
  results: z.array(mediaSearchResultSchema),
});
export type MediaSearchResponse = z.infer<typeof mediaSearchResponseSchema>;

// ---------- schedule v2: host auth (worker-proxied magic links) ----------
// The worker IS the auth broker: it mints magic links via the Supabase admin
// API (service role), emails them itself (Resend), and verifies/refreshes
// sessions on the SPA's behalf. No anon key, no supabase-js anywhere.

export const authLoginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
});
export type AuthLogin = z.infer<typeof authLoginSchema>;

export const authVerifySchema = z.object({
  token_hash: z.string().trim().min(10).max(300),
});
export type AuthVerify = z.infer<typeof authVerifySchema>;

export const authRefreshSchema = z.object({
  refresh_token: z.string().trim().min(6).max(600),
});
export type AuthRefresh = z.infer<typeof authRefreshSchema>;

/** What /api/auth/verify and /api/auth/refresh hand back on success. */
export const authSessionSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
  expires_at: z.number().int(), // unix seconds
  email: z.string(),
});
export type AuthSession = z.infer<typeof authSessionSchema>;

// ---------- device-code pairing (how the GAME signs in) ----------

/** POST /api/device/start response. deviceCode is the game's secret half and
 *  must never be displayed; userCode is the half the player reads off the
 *  headset and types on their phone. */
export const deviceStartSchema = z.object({
  deviceCode: z.string(),
  userCode: z.string(),
  expiresAt: z.string(),
  intervalSeconds: z.number().int().positive(),
});
export type DeviceStart = z.infer<typeof deviceStartSchema>;

/** POST /api/device/claim — the signed-in phone binds a code to its account. */
export const deviceClaimSchema = z.object({
  userCode: z.string().trim().min(4).max(12),
});
export type DeviceClaim = z.infer<typeof deviceClaimSchema>;

export const devicePollRequestSchema = z.object({
  deviceCode: z.string().trim().min(10).max(200),
});
export type DevicePollRequest = z.infer<typeof devicePollRequestSchema>;

/** 'pending' = keep polling; 'expired' = start over; 'ready' = signed in.
 *  There is deliberately no 'denied' — an unknown code reports as expired so
 *  polling can never be used to probe which codes exist. */
export const devicePollSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('pending') }),
  z.object({ status: z.literal('expired') }),
  z.object({ status: z.literal('ready'), session: authSessionSchema }),
]);
export type DevicePoll = z.infer<typeof devicePollSchema>;

/** /api/auth/login always answers ok:true (no user enumeration); `sent` +
 * `reason` describe the mail pipeline ('mail-unconfigured' until the owner
 * sets RESEND_API_KEY). */
export const authLoginResultSchema = z.object({
  ok: z.literal(true),
  sent: z.boolean(),
  reason: z.string().optional(),
});
export type AuthLoginResult = z.infer<typeof authLoginResultSchema>;

// ---------- schedule v2: permanent friends + host prefs ----------

export const attendModeSchema = z.enum(['vr', 'browser']);
export type AttendMode = z.infer<typeof attendModeSchema>;

const emailField = z.string().trim().toLowerCase().email().max(200);
// Digits with common separators; the worker strips to digits before use.
const phoneField = z.string().trim().regex(/^\+?[\d\s().-]{7,20}$/, 'not a phone number');
// A carrier email-to-SMS gateway DOMAIN (e.g. vtext.com). Never guessed.
const gatewayField = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'not a domain')
  .max(80);

export const friendInputSchema = z.object({
  name: trimmed(MAX_NAME_LEN),
  email: emailField.optional(),
  phone: phoneField.optional(),
  carrierGateway: gatewayField.optional(),
  attendMode: attendModeSchema.default('vr'),
  favorite: z.boolean().default(false),
});
export type FriendInput = z.infer<typeof friendInputSchema>;

export const friendSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  carrierGateway: z.string().optional(),
  attendMode: attendModeSchema,
  favorite: z.boolean(),
  createdAt: z.string(),
});
export type Friend = z.infer<typeof friendSchema>;

export const friendsListSchema = z.object({ friends: z.array(friendSchema) });
export type FriendsList = z.infer<typeof friendsListSchema>;

/** POST /api/friends/update — id + any fields to change; null clears a field. */
export const friendUpdateSchema = z.object({
  id: z.string().uuid(),
  name: trimmed(MAX_NAME_LEN).optional(),
  email: emailField.nullable().optional(),
  phone: phoneField.nullable().optional(),
  carrierGateway: gatewayField.nullable().optional(),
  attendMode: attendModeSchema.optional(),
  favorite: z.boolean().optional(),
});
export type FriendUpdate = z.infer<typeof friendUpdateSchema>;

export const friendRemoveSchema = z.object({ id: z.string().uuid() });
export type FriendRemove = z.infer<typeof friendRemoveSchema>;

// ---------- schedule v2: friend groups ----------

/** A named, reusable selection of saved friends. Groups are a SELECTION
 * convenience only: the create flow expands one into friend ids and rides the
 * existing invite path, so nothing in the send/snapshot/reminder machinery
 * needs to know groups exist. Membership is capped at the invite ceiling —
 * a group you cannot invite in one tap would defeat the point. */
export const friendGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  memberIds: z.array(z.string().uuid()),
  createdAt: z.string(),
});
export type FriendGroup = z.infer<typeof friendGroupSchema>;

export const friendGroupsListSchema = z.object({ groups: z.array(friendGroupSchema) });
export type FriendGroupsList = z.infer<typeof friendGroupsListSchema>;

export const friendGroupInputSchema = z.object({
  name: trimmed(MAX_NAME_LEN),
  memberIds: z.array(z.string().uuid()).max(MAX_INVITEES).default([]),
});
export type FriendGroupInput = z.infer<typeof friendGroupInputSchema>;

/** POST /api/groups/update — id + any field to change. */
export const friendGroupUpdateSchema = z.object({
  id: z.string().uuid(),
  name: trimmed(MAX_NAME_LEN).optional(),
  memberIds: z.array(z.string().uuid()).max(MAX_INVITEES).optional(),
});
export type FriendGroupUpdate = z.infer<typeof friendGroupUpdateSchema>;

export const friendGroupRemoveSchema = z.object({ id: z.string().uuid() });
export type FriendGroupRemove = z.infer<typeof friendGroupRemoveSchema>;

/** GET /api/prefs response. */
export const prefsSchema = z.object({
  email: z.string().optional(),
  /** THU-72 deviation 1: the name invites say is hosting. Absent = no clause. */
  displayName: z.string().optional(),
  defaultRoomCode: z.string().optional(),
  discordWebhook: z.string().optional(),
});
export type Prefs = z.infer<typeof prefsSchema>;

/** POST /api/prefs — partial update; null clears a field. */
export const prefsUpdateSchema = z.object({
  // Capped at 40 to match the other free-text pref. notify.ts trims it again to
  // 24 for the wire, because a 40-char name in a 160-char SMS is a different
  // problem from a 40-char name in a database.
  displayName: z.string().trim().max(40).nullable().optional(),
  defaultRoomCode: z.string().trim().max(40).nullable().optional(),
  discordWebhook: z
    .string()
    .url()
    .startsWith('https://discord.com/api/webhooks/')
    .max(300)
    .nullable()
    .optional(),
});
export type PrefsUpdate = z.infer<typeof prefsUpdateSchema>;

// ---------- schedule v2: invite sends ----------

/** One recipient for POST /api/events/:id/send — a saved friend by id (needs
 * the host's bearer) OR inline contact details for a one-off invitee. */
export const sendInviteEntrySchema = z
  .object({
    friendId: z.string().uuid().optional(),
    name: trimmed(MAX_NAME_LEN).optional(),
    email: emailField.optional(),
    phone: phoneField.optional(),
    carrierGateway: gatewayField.optional(),
    attendMode: attendModeSchema.optional(),
  })
  .refine((e) => Boolean(e.friendId || e.name), { message: 'friendId or name required' });
export type SendInviteEntry = z.infer<typeof sendInviteEntrySchema>;

export const sendInvitesSchema = z.object({
  invites: z.array(sendInviteEntrySchema).min(1).max(MAX_INVITEES),
});
export type SendInvites = z.infer<typeof sendInvitesSchema>;

export const sendChannelResultSchema = z.object({
  sent: z.boolean(),
  reason: z.string().optional(),
  /** true when the mail rode the onboarding@resend.dev fallback sender —
   * Resend delivers those ONLY to the account owner's inbox. */
  fromFallback: z.boolean().optional(),
  /** human-readable fail-loud note that travels with a fallback send. */
  warning: z.string().optional(),
});
export const sendRecipientSchema = z.object({
  name: z.string(),
  voterCode: z.string().optional(),
  email: sendChannelResultSchema.optional(),
  sms: sendChannelResultSchema.optional(),
  reason: z.string().optional(), // why skipped (e.g. 'no-contact')
});
export type SendRecipient = z.infer<typeof sendRecipientSchema>;

/**
 * `sent` means AT LEAST ONE CHANNEL ACTUALLY DELIVERED. It used to mean
 * "delivery was attempted", which is how a night with no Resend key told the
 * host "Invites are out." over five invitations that were never sent (THU-185).
 * A recipient every channel failed for goes in `failed`, with a reason a person
 * can read; one with no contact details at all stays in `skipped`.
 */
export const sendInvitesResultSchema = z.object({
  sent: z.array(sendRecipientSchema),
  failed: z.array(sendRecipientSchema),
  skipped: z.array(sendRecipientSchema),
});
export type SendInvitesResult = z.infer<typeof sendInvitesResultSchema>;

/** GET /api/invites/:code — resolve an /i/ short link to the vote page params. */
export const inviteResolveSchema = z.object({
  eventId: z.string(),
  voterCode: z.string(),
});
export type InviteResolve = z.infer<typeof inviteResolveSchema>;

// ---------- schedule v2: the game's TONIGHT banner ----------

/** GET /api/room/:code/next — {} when nothing is on (always 200). */
export const nightNextSchema = z.object({
  eventTitle: z.string().optional(),
  movieTitle: z.string().optional(),
  startsAt: z.string().optional(),
  roomCode: z.string().optional(),
});
export type NightNext = z.infer<typeof nightNextSchema>;

// ---------- errors ----------

export const errorSchema = z.object({
  error: z.string(),
  message: z.string(),
});
export type ApiError = z.infer<typeof errorSchema>;

/** Parse helper: returns data or throws a friendly ApiError-shaped object. */
export function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, what: string): T {
  const r = schema.safeParse(raw);
  if (!r.success) {
    throw { error: 'BAD_INPUT', message: `Invalid ${what}.` } satisfies ApiError;
  }
  return r.data;
}

// ---------- THU-115: clickwrap assent ----------
// Terms that are merely posted are browsewrap and routinely unenforceable. What
// makes them stick is an affirmative act, recorded with a timestamp and a
// version — a stored boolean is worthless the first time the terms change.
//
// THE VERSION IS THE DOCUMENT'S OWN "Last updated" DATE, not a number somebody
// remembers to bump. Both published pages already carry one, and Terms s15
// promises a new one on every material change, so the date is the identifier
// the terms themselves define. `tools/tests/terms-assent.test.js` reads the
// dates back out of terms.html and privacy.html and fails if either constant
// drifts from the page it claims to describe — the failure mode being guarded is
// silent and total: edit the Terms, and every recorded acceptance keeps
// reporting as current for a document nobody ever saw.
export const LEGAL_TERMS_VERSION = '2026-08-19';
export const LEGAL_PRIVACY_VERSION = '2026-09-12';

export const LEGAL_TERMS_URL = 'https://thursday-night.com/terms';
export const LEGAL_PRIVACY_URL = 'https://thursday-night.com/privacy';

/**
 * GET /api/auth/terms — does this signed-in person still owe us an agreement?
 *
 * `storeAvailable: false` means the acceptance table could not be reached (not
 * applied yet, or Supabase is down). The server answers `required: false` in
 * that case ON PURPOSE: a legal gate that cannot record anything provides no
 * evidence whatsoever and would lock every host out of their own site. It fails
 * open, and says so, rather than failing shut and silent.
 */
export const termsStatusSchema = z.object({
  required: z.boolean(),
  storeAvailable: z.boolean(),
  termsVersion: z.string(),
  privacyVersion: z.string(),
  termsUrl: z.string(),
  privacyUrl: z.string(),
  /** ISO timestamp of the accepted-this-version row, when there is one. */
  acceptedAt: z.string().nullable(),
});
export type TermsStatus = z.infer<typeof termsStatusSchema>;

/**
 * POST /api/auth/terms/accept — the client echoes back the versions it actually
 * displayed. The server refuses anything that isn't current, because a tab left
 * open across a terms change would otherwise record assent to wording its user
 * was never shown, which is precisely the evidence this endpoint exists to make.
 */
export const termsAcceptSchema = z.object({
  termsVersion: z.string().min(1).max(40),
  privacyVersion: z.string().min(1).max(40),
});
export type TermsAccept = z.infer<typeof termsAcceptSchema>;

export const termsAcceptResultSchema = z.object({
  ok: z.literal(true),
  acceptedAt: z.string(),
});
export type TermsAcceptResult = z.infer<typeof termsAcceptResultSchema>;
